/**
 * Sombra do cenário pré-calculada (item 3.2). O estádio não se mexe, então a sombra que ele projeta no chão
 * é renderizada uma vez numa textura vista de cima. Depois disso as peças do cenário saem da passada de sombra
 * e o mapa de sombra em tempo real fica só para atletas e bolas, cobrindo uma área pequena que segue a jogada.
 */
import * as THREE from "three";

/** Área do chão coberta pela textura, em metros: [x mínimo, z mínimo, largura, profundidade]. */
export const BAKE_RECT = [-170, -150, 340, 300] as const;

export interface BakeUniforms {
  uBake: THREE.IUniform<THREE.Texture | null>;
  uBakeRect: THREE.IUniform<THREE.Vector4>;
}

export function bakeUniforms(): BakeUniforms {
  return { uBake: { value: null }, uBakeRect: { value: new THREE.Vector4(...BAKE_RECT) } };
}

/**
 * Faz o material multiplicar a luz direta do sol pela sombra pré-calculada. A sombra dos atletas (mapa em tempo
 * real) continua valendo por cima.
 */
export function useBakedShadow(material: THREE.Material, uniforms: BakeUniforms): void {
  const previous = material.onBeforeCompile.bind(material);
  material.userData["bake"] = uniforms;
  material.onBeforeCompile = (s, r) => {
    previous(s, r);
    Object.assign(s.uniforms, uniforms);
    s.vertexShader =
      "varying vec2 vBakeXZ;\n" +
      s.vertexShader.replace(
        "#include <begin_vertex>",
        "#include <begin_vertex>\n vBakeXZ = (modelMatrix * vec4(transformed, 1.0)).xz;",
      );
    s.fragmentShader =
      "uniform sampler2D uBake;\nuniform vec4 uBakeRect;\nvarying vec2 vBakeXZ;\n" +
      s.fragmentShader.replace(
        "#include <lights_fragment_end>",
        `#include <lights_fragment_end>
        vec2 bakeUv = (vBakeXZ - uBakeRect.xy) / uBakeRect.zw;
        float baked = (bakeUv.x < 0.0 || bakeUv.y < 0.0 || bakeUv.x > 1.0 || bakeUv.y > 1.0) ? 1.0 : texture2D(uBake, bakeUv).r;
        reflectedLight.directDiffuse *= baked;`,
      );
  };
  const key = material.customProgramCacheKey.bind(material);
  material.customProgramCacheKey = () => `${key()}-sombra-assada`;
  material.needsUpdate = true;
}

/**
 * Renderiza a sombra que `casters` projetam no plano y = 0 vindo da direção `sunDirection` (do chão para o sol).
 * Devolve uma textura de um canal: 1 = sol, 0 = sombra.
 */
export function bakeGroundShadow(
  renderer: THREE.WebGLRenderer,
  casters: THREE.Object3D,
  sunDirection: THREE.Vector3,
  { size = 2048, shadowSize = 4096 } = {},
): THREE.WebGLRenderTarget {
  const [x0, z0, w, d] = BAKE_RECT;
  const center = new THREE.Vector3(x0 + w / 2, 0, z0 + d / 2);
  const light = new THREE.DirectionalLight(0xffffff, 1);
  light.position.copy(center).addScaledVector(sunDirection.clone().normalize(), 260);
  light.target.position.copy(center);
  light.castShadow = true;
  light.shadow.mapSize.set(shadowSize, shadowSize);
  const half = Math.max(w, d) * 0.62;
  Object.assign(light.shadow.camera, {
    left: -half,
    right: half,
    top: half,
    bottom: -half,
    near: 1,
    far: 600,
  });
  light.shadow.bias = -0.0005;
  light.shadow.radius = 2;

  const parent = casters.parent;
  const scratch = new THREE.WebGLRenderTarget(1, 1);
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const prevTarget = renderer.getRenderTarget();
  const prevAuto = renderer.shadowMap.autoUpdate;
  // O preset Baixo desliga a sombra em tempo real, mas o cálculo único precisa dela ligada.
  const prevEnabled = renderer.shadowMap.enabled;
  renderer.shadowMap.enabled = true;

  // 1) Mapa de sombra só com o cenário.
  const castScene = new THREE.Scene();
  castScene.add(casters, light, light.target);
  renderer.shadowMap.autoUpdate = false;
  renderer.shadowMap.needsUpdate = true;
  renderer.setRenderTarget(scratch);
  renderer.render(castScene, camera);

  // 2) Plano do chão desenhado no espaço da textura, lendo esse mapa de sombra.
  // Dupla face: o mapeamento para o espaço da textura inverte o sentido dos triângulos.
  const plane = new THREE.Mesh(
    new THREE.PlaneGeometry(w, d),
    new THREE.ShadowMaterial({ side: THREE.DoubleSide }),
  );
  plane.geometry.rotateX(-Math.PI / 2);
  plane.position.copy(center);
  plane.receiveShadow = true;
  plane.frustumCulled = false;
  plane.material.onBeforeCompile = (s) => {
    s.uniforms["uBakeRect"] = { value: new THREE.Vector4(...BAKE_RECT) };
    s.vertexShader =
      "uniform vec4 uBakeRect;\n" +
      s.vertexShader.replace(
        "#include <fog_vertex>",
        `#include <fog_vertex>
        vec2 bakeXZ = (modelMatrix * vec4(transformed, 1.0)).xz;
        gl_Position = vec4((bakeXZ - uBakeRect.xy) / uBakeRect.zw * 2.0 - 1.0, 0.0, 1.0);`,
      );
    s.fragmentShader = s.fragmentShader.replace(
      "gl_FragColor = vec4( color, opacity * ( 1.0 - getShadowMask() ) );",
      "gl_FragColor = vec4( vec3( getShadowMask() ), 1.0 );",
    );
  };
  const target = new THREE.WebGLRenderTarget(size, size, { depthBuffer: false, format: THREE.RedFormat });
  target.texture.minFilter = THREE.LinearFilter;
  target.texture.generateMipmaps = false;
  const bakeScene = new THREE.Scene();
  bakeScene.add(plane, light, light.target);
  const prevClear = renderer.getClearColor(new THREE.Color());
  const prevAlpha = renderer.getClearAlpha();
  renderer.setClearColor(0xffffff, 1);
  renderer.setRenderTarget(target);
  renderer.clear();
  renderer.render(bakeScene, camera);
  renderer.setClearColor(prevClear, prevAlpha);

  // Devolve tudo como estava.
  renderer.setRenderTarget(prevTarget);
  renderer.shadowMap.autoUpdate = prevAuto;
  renderer.shadowMap.enabled = prevEnabled;
  renderer.shadowMap.needsUpdate = true;
  if (parent) parent.add(casters);
  light.shadow.dispose();
  plane.geometry.dispose();
  plane.material.dispose();
  scratch.dispose();
  return target;
}
